// src/components/clients/AssignUsersModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Checkbox,
  TextField,
  Chip,
  InputAdornment,
  useTheme,
  alpha
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

import useUserStore from '../../stores/userStore';
import useClientStore from '../../stores/clientStore';
import useNotificationStore from '../../stores/notificationStore';
import { assignUsersToClient } from '../../services/clientService';
import { MagicLoader, Button } from '../../design-system';
import { useThemeTokens } from '../../theme/themeHooks';

const AssignUsersModal = ({ open, onClose, clientId }) => {
  const theme = useTheme();
  const { tokens } = useThemeTokens();
  const { showSuccess, showError } = useNotificationStore();
  const { fetchUsers, users, isLoading: isLoadingUsers } = useUserStore();
  const { fetchClientUsers, isLoading: isLoadingClientUsers } = useClientStore();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [existingUserIds, setExistingUserIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Fetch all users and client's existing users on mount
  useEffect(() => {
    if (open) {
      fetchUsers();
      
      // Get existing client users to exclude them
      const loadClientUsers = async () => {
        try {
          const clientUsers = await fetchClientUsers(clientId);
          if (clientUsers) {
            const existingIds = clientUsers.map(user => user._id);
            setExistingUserIds(existingIds);
          }
        } catch (error) {
          console.error('Error loading client users:', error);
        }
      };
      
      loadClientUsers();
    }
  }, [open, clientId, fetchUsers, fetchClientUsers]);
  
  // Filter users based on search and existing memberships
  const filteredUsers = users.filter(user => {
    // Filter out users already assigned to this client
    if (existingUserIds.includes(user._id)) {
      return false;
    }
    
    // Apply search filter if search term exists
    if (searchTerm) {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      return fullName.includes(searchTerm.toLowerCase()) || 
             (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    
    return true;
  });
  
  // Handle user selection
  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (selectedUsers.length === 0) {
      showError('Please select at least one user to assign');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await assignUsersToClient(clientId, selectedUsers);
      showSuccess(`Successfully assigned ${selectedUsers.length} user(s) to client`);
      
      // Reset selections and close modal
      setSelectedUsers([]);
      onClose(true); // Pass true to indicate successful update
    } catch (error) {
      showError('Failed to assign users: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={() => onClose(false)} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: tokens.borderRadius.card,
          background: theme.aghrba.colors.shadows.cardShadow(theme.palette.mode),
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" fontWeight={600}>
          Assign Users to Client
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search users by name or email"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: tokens.borderRadius.button,
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                transition: tokens.transitions.standard,
                '&:hover': {
                  boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                },
                '&.Mui-focused': {
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.3)}`,
                },
              },
            }}
          />
        </Box>
        
        {selectedUsers.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
              Selected Users ({selectedUsers.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedUsers.map(userId => {
                const user = users.find(u => u._id === userId);
                return user ? (
                  <Chip 
                    key={userId}
                    label={`${user.firstName} ${user.lastName}`}
                    onDelete={() => handleUserToggle(userId)}
                    sx={{ 
                      borderRadius: tokens.borderRadius.small,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      '& .MuiChip-deleteIcon': {
                        color: theme.palette.primary.main,
                      }
                    }}
                  />
                ) : null;
              })}
            </Box>
          </Box>
        )}
        
        {isLoadingUsers || isLoadingClientUsers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <MagicLoader size="medium" message="Loading available users..." />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            backgroundColor: alpha(theme.palette.background.default, 0.5),
            borderRadius: tokens.borderRadius.card,
          }}>
            <Typography color="textSecondary" variant="h6" gutterBottom>
              {searchTerm 
                ? 'No matching users found' 
                : 'All users are already assigned to this client'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ 
            maxHeight: 400, 
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: alpha(theme.palette.text.disabled, 0.1),
              borderRadius: tokens.borderRadius.small,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.text.disabled, 0.3),
              borderRadius: tokens.borderRadius.small,
              '&:hover': {
                backgroundColor: alpha(theme.palette.text.disabled, 0.5),
              },
            },
          }}>
            {filteredUsers.map(user => (
              <ListItem 
                key={user._id} 
                button 
                onClick={() => handleUserToggle(user._id)}
                sx={{
                  borderRadius: tokens.borderRadius.medium,
                  mb: 1,
                  transition: tokens.transitions.standard,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                <Checkbox 
                  edge="start"
                  checked={selectedUsers.includes(user._id)}
                  tabIndex={-1}
                  disableRipple
                  
                  sx={{ mr: 1 }}
                />
                <ListItemAvatar>
                  <Avatar 
                    src={user.photo}
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      borderRadius: tokens.borderRadius.medium,
                    }}
                  >
                    {user.firstName?.[0]}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText 
                  primary={
                    <Typography fontWeight={500}>
                      {user.firstName} {user.lastName}
                    </Typography>
                  }
                  secondary={
                    <>
                      {user.email}<br />
                      <Chip 
                        size="small" 
                        label={user.role}
                        
                        variant="outlined"
                        sx={{ 
                          mt: 0.5,
                          borderRadius: tokens.borderRadius.small,
                          height: 22
                        }}
                      />
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={() => onClose(false)}
          variant="outlined"
          
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          
          disabled={selectedUsers.length === 0 || isSubmitting}
          magical={true}
        >
          {isSubmitting ? 'Assigning...' : 'Assign Users'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignUsersModal;